import type { CameraState, ViewCamera } from '../types';
import type { TrackballSphere } from '../geometry/trackball';
import {
  isBrowserZoomGesture,
  isSameCamera,
  KEYBOARD_STEP,
  orbitReducer,
  RADIANS_PER_PIXEL,
  toPixelDelta,
  ZOOM_KEY_DELTA,
  type DragBall,
  type OrbitGesture,
  type OrbitState,
} from './orbitCamera';

/**
 * Which input opened the drag `orbit.drag` records, and the keyboard's accumulated offset.
 *
 * A union rather than a flag beside a flag, so "the pointer is dragging and the keyboard is too" is
 * a state that cannot be written down.
 */
type GestureOwner =
  | { owner: 'pointer' }
  | {
      owner: 'keyboard';
      offset: { x: number; y: number };
      /** Whether a key has begun repeating, which is what separates a hold from a tap. */
      repeating: boolean;
    };

export type OrbitGestureState<C extends ViewCamera = CameraState> = {
  orbit: OrbitState<C>;
  gesture: GestureOwner | null;
  /** Keyed on `event.key`. An arrow has no shifted form. */
  heldRotationKeys: ReadonlySet<string>;
  /**
   * Keyed on `event.code`: `key` is what the modifiers make of a physical key, so a `+` released
   * after Shift reports `'='` and would never leave the set.
   */
  heldZoomKeys: ReadonlySet<string>;
  zoomRepeating: boolean;
  /** A camera the host supplied while a gesture was open, applied when it closes. */
  pendingCamera: C | null;
  /**
   * Whether a reader input has changed the camera by value since the host last set one.
   *
   * A press that turns nothing and a zoom already at its limit leave it alone. A host-supplied
   * camera clears it: the flag exists so a host that supplies a camera knows when to stop, and a
   * host that has just *set* one has not been overridden yet.
   */
  hasReaderMovedCamera: boolean;
};

export type OrbitInput<C extends ViewCamera = CameraState> =
  | {
      type: 'pointerDown';
      x: number;
      y: number;
      /** The canvas's trackball, in the same pixels as `x` and `y`. */
      sphere: TrackballSphere;
    }
  | { type: 'pointerMove'; x: number; y: number }
  | { type: 'pointerUp' }
  | { type: 'pointerCancel' }
  | { type: 'wheel'; delta: number }
  | {
      type: 'rotateKey';
      key: string;
      yaw: number;
      pitch: number;
      repeat: boolean;
    }
  | { type: 'zoomKey'; code: string; delta: number; repeat: boolean }
  | { type: 'keyUp'; key: string; code: string }
  | { type: 'blur' }
  /** The element carrying the gesture has gone away, which delivers no `pointerup`, `keyup` or `blur`. */
  | { type: 'detach' }
  | { type: 'setCamera'; camera: C };

/** Every input that comes from the reader rather than the host, and so carries no camera. */
export type ReaderInput = Exclude<OrbitInput, { type: 'setCamera' }>;

type KeyLike = Pick<
  KeyboardEvent,
  'key' | 'code' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'repeat'
>;

const ROTATION_KEYS = new Map<string, { yaw: number; pitch: number }>([
  ['ArrowLeft', { yaw: -1, pitch: 0 }],
  ['ArrowRight', { yaw: 1, pitch: 0 }],
  ['ArrowUp', { yaw: 0, pitch: -1 }],
  ['ArrowDown', { yaw: 0, pitch: 1 }],
]);

const ZOOM_KEYS = new Map<string, number>([
  ['+', -ZOOM_KEY_DELTA],
  ['=', -ZOOM_KEY_DELTA],
  ['-', ZOOM_KEY_DELTA],
]);

const EMPTY_KEYS: ReadonlySet<string> = new Set();

export const initialOrbitGestureState = <C extends ViewCamera>(
  camera: C
): OrbitGestureState<C> => ({
  orbit: { camera, drag: null },
  gesture: null,
  heldRotationKeys: EMPTY_KEYS,
  heldZoomKeys: EMPTY_KEYS,
  zoomRepeating: false,
  pendingCamera: null,
  hasReaderMovedCamera: false,
});

/**
 * Primary button only. A secondary press opens a drag that nothing closes - the browser hands the
 * following `pointerup` to the context menu - and a secondary release while the primary is held
 * would end a rotation the reader is still making.
 */
export const isOrbitButton = ({
  button,
}: Pick<MouseEvent, 'button'>): boolean => button === 0;

/**
 * The input a key stands for, or null for a key the chart leaves to the page.
 *
 * Ctrl and Cmd chords belong to the browser, as they do for the wheel. macOS also delivers no
 * `keyup` for a key released while Cmd is down, so recording one would strand it.
 */
export const toKeyDownInput = (event: KeyLike): ReaderInput | null => {
  if (event.ctrlKey || event.metaKey) return null;
  const rotation = ROTATION_KEYS.get(event.key);
  if (rotation) {
    const step = event.shiftKey ? KEYBOARD_STEP * 3 : KEYBOARD_STEP;
    return {
      type: 'rotateKey',
      key: event.key,
      yaw: rotation.yaw * step,
      pitch: rotation.pitch * step,
      repeat: event.repeat,
    };
  }
  const delta = ZOOM_KEYS.get(event.key);
  if (delta === undefined) return null;
  return { type: 'zoomKey', code: event.code, delta, repeat: event.repeat };
};

export const toKeyUpInput = ({
  key,
  code,
}: Pick<KeyboardEvent, 'key' | 'code'>): ReaderInput => ({
  type: 'keyUp',
  key,
  code,
});

/** Null for a wheel the browser owns, which the caller must leave un-prevented. */
export const toWheelInput = (event: WheelEvent): ReaderInput | null => {
  if (isBrowserZoomGesture(event)) return null;
  return { type: 'wheel', delta: toPixelDelta(event) };
};

/** A rotation is under way, by pointer or by keyboard - including a press that has not moved. */
export const isRotating = ({
  gesture,
}: OrbitGestureState<ViewCamera>): boolean => gesture !== null;

/** The plot is actually moving, as opposed to merely being held. */
export const isTurning = ({ orbit }: OrbitGestureState<ViewCamera>): boolean =>
  orbit.drag?.hasTurned === true;

/**
 * A pointer drag qualifies once it has moved; a keyboard one only once a key has begun repeating,
 * since a tap is how a reader aims.
 */
export const isSustainedRotation = ({
  gesture,
  orbit,
}: OrbitGestureState<ViewCamera>): boolean => {
  if (gesture?.owner === 'keyboard') return gesture.repeating;
  return orbit.drag?.hasTurned === true;
};

export const hasKeyboardRotationStarted = (
  previous: OrbitGestureState<ViewCamera>,
  next: OrbitGestureState<ViewCamera>
): boolean =>
  previous.gesture?.owner !== 'keyboard' && next.gesture?.owner === 'keyboard';

export const hasKeyboardRotationEnded = (
  previous: OrbitGestureState<ViewCamera>,
  next: OrbitGestureState<ViewCamera>
): boolean => previous.gesture?.owner === 'keyboard' && next.gesture === null;

export const hasKeyboardZoomEnded = (
  previous: OrbitGestureState<ViewCamera>,
  next: OrbitGestureState<ViewCamera>
): boolean => previous.zoomRepeating && !next.zoomRepeating;

const applyOrbit = <C extends ViewCamera>(
  state: OrbitGestureState<C>,
  gesture: OrbitGesture<C>
): OrbitGestureState<C> => {
  const orbit = orbitReducer(state.orbit, gesture);
  if (orbit === state.orbit) return state;

  const hasMovedCamera = !isSameCamera(orbit.camera, state.orbit.camera);
  const hasReaderMovedCamera = state.hasReaderMovedCamera || hasMovedCamera;

  /* A deferred host camera is obsolete the moment the reader moves the camera themselves. Applied
     when the gesture closes it would replace the rotation they just made with the view they had
     before it, which is the whole gesture undone on release. */
  const pendingCamera = hasMovedCamera ? null : state.pendingCamera;

  if (orbit.drag === null && pendingCamera) {
    return {
      ...state,
      orbit: { ...orbit, camera: pendingCamera },
      pendingCamera: null,
      hasReaderMovedCamera,
    };
  }
  return { ...state, orbit, pendingCamera, hasReaderMovedCamera };
};

const withoutKey = (
  keys: ReadonlySet<string>,
  key: string
): ReadonlySet<string> => {
  if (!keys.has(key)) return keys;
  const next = new Set(keys);
  next.delete(key);
  return next;
};

const withKey = (
  keys: ReadonlySet<string>,
  key: string
): ReadonlySet<string> => (keys.has(key) ? keys : new Set(keys).add(key));

const endGestureOf = <C extends ViewCamera>(
  state: OrbitGestureState<C>,
  owner: GestureOwner['owner']
): OrbitGestureState<C> => {
  if (state.gesture?.owner !== owner) return state;
  return applyOrbit({ ...state, gesture: null }, { type: 'dragEnd' });
};

const endKeyboardRotation = <C extends ViewCamera>(
  state: OrbitGestureState<C>
): OrbitGestureState<C> =>
  endGestureOf(
    state.heldRotationKeys.size === 0
      ? state
      : { ...state, heldRotationKeys: EMPTY_KEYS },
    'keyboard'
  );

const endKeyboardZoom = <C extends ViewCamera>(
  state: OrbitGestureState<C>
): OrbitGestureState<C> => {
  if (state.heldZoomKeys.size === 0 && !state.zoomRepeating) return state;
  return { ...state, heldZoomKeys: EMPTY_KEYS, zoomRepeating: false };
};

/** Sized so a key step turns a trackball by the same angle it turns a turntable. */
const KEYBOARD_BALL: DragBall = {
  kind: 'underAnchor',
  radius: 1 / RADIANS_PER_PIXEL,
};

const rotateByKey = <C extends ViewCamera>(
  state: OrbitGestureState<C>,
  { key, yaw, pitch, repeat }: Extract<ReaderInput, { type: 'rotateKey' }>
): OrbitGestureState<C> => {
  if (state.gesture?.owner === 'pointer') return state;

  const opened =
    state.gesture?.owner === 'keyboard'
      ? state
      : applyOrbit(
          {
            ...state,
            gesture: {
              owner: 'keyboard',
              offset: { x: 0, y: 0 },
              repeating: false,
            },
          },
          { type: 'dragStart', x: 0, y: 0, ball: KEYBOARD_BALL }
        );
  if (opened.gesture?.owner !== 'keyboard') return opened;

  const offset = {
    x: opened.gesture.offset.x + yaw / RADIANS_PER_PIXEL,
    y: opened.gesture.offset.y + pitch / RADIANS_PER_PIXEL,
  };
  return applyOrbit(
    {
      ...opened,
      gesture: {
        owner: 'keyboard',
        offset,
        repeating: opened.gesture.repeating || repeat,
      },
      heldRotationKeys: withKey(opened.heldRotationKeys, key),
    },
    { type: 'dragMove', ...offset }
  );
};

/**
 * The gesture layer above `orbitReducer`, shared by `SurfaceChartController` and
 * `useOrbitInteraction` so a fix to how the inputs combine reaches both.
 *
 * A keyboard rotation holds one drag open across key repeats and accumulates its offset, because
 * `dragMove` carries an absolute coordinate. It is ignored while the pointer owns the drag, and
 * only the pointer's own events move or end a pointer drag.
 */
export const reduceOrbitGesture = <C extends ViewCamera>(
  state: OrbitGestureState<C>,
  input: OrbitInput<C>
): OrbitGestureState<C> => {
  switch (input.type) {
    case 'pointerDown':
      return applyOrbit(
        { ...state, gesture: { owner: 'pointer' } },
        {
          type: 'dragStart',
          x: input.x,
          y: input.y,
          ball: { kind: 'fixed', sphere: input.sphere },
        }
      );
    case 'pointerMove':
      if (state.gesture?.owner !== 'pointer') return state;
      return applyOrbit(state, { type: 'dragMove', x: input.x, y: input.y });
    case 'pointerUp':
    case 'pointerCancel':
      return endGestureOf(state, 'pointer');
    case 'wheel':
      return applyOrbit(state, { type: 'wheel', delta: input.delta });
    case 'rotateKey':
      return rotateByKey(state, input);
    case 'zoomKey':
      return applyOrbit(
        {
          ...state,
          heldZoomKeys: withKey(state.heldZoomKeys, input.code),
          zoomRepeating: state.zoomRepeating || input.repeat,
        },
        { type: 'wheel', delta: input.delta }
      );
    case 'keyUp': {
      /* macOS drops the `keyup` of any key released while Cmd is down, including one pressed
         before it, so Cmd's own release is the last word on everything held. */
      if (input.key === 'Meta') {
        return endKeyboardRotation(endKeyboardZoom(state));
      }
      const heldZoomKeys = withoutKey(state.heldZoomKeys, input.code);
      const heldRotationKeys = withoutKey(state.heldRotationKeys, input.key);
      const released =
        heldZoomKeys === state.heldZoomKeys &&
        heldRotationKeys === state.heldRotationKeys
          ? state
          : { ...state, heldZoomKeys, heldRotationKeys };
      const zoomSettled =
        heldZoomKeys.size === 0 ? endKeyboardZoom(released) : released;
      if (heldRotationKeys.size > 0) return zoomSettled;
      return endKeyboardRotation(zoomSettled);
    }
    case 'blur':
      return endKeyboardRotation(endKeyboardZoom(state));
    case 'detach':
      return endGestureOf(
        endKeyboardRotation(endKeyboardZoom(state)),
        'pointer'
      );
    case 'setCamera': {
      if (state.orbit.drag) return { ...state, pendingCamera: input.camera };
      const orbit = orbitReducer(state.orbit, input);
      if (
        orbit === state.orbit &&
        state.pendingCamera === null &&
        !state.hasReaderMovedCamera
      ) {
        return state;
      }
      /* The latch clears here, which is what makes it mean "since the camera the host last set"
         rather than "ever". A host supplies a camera while the reader has not taken it and stops
         once they have; without this, a host that puts the camera back - a fit control - would
         find itself locked out of its own view for good, and a resize after a fit would frame the
         graph to the viewport it had before. */
      return {
        ...state,
        orbit,
        pendingCamera: null,
        hasReaderMovedCamera: false,
      };
    }
  }
};
